import { REST_SPEED, RESTITUTION, SEPARATION_ITERATIONS, TICK_HZ } from './constants';
import { moveCircle } from './tilemap';
import type { Body, Circle, Pillar, TileMap, Vec2 } from './types';

// Hand-rolled physics for circles on a tile grid. Every function mutates the body it is given; the
// caller (sim.ts) works on a cloned state draft.

/**
 * v += dir * accel, then friction. The speed cap limits what the input can build up: a body that an
 * impulse (recoil, knockback) pushed beyond maxSpeed keeps that speed and friction alone brings it back,
 * so the input may brake or steer but never boost past the decaying impulse. `dir` must already be
 * clamped to length <= 1.
 */
export function accelerate(body: Body, dir: Vec2, accel: number, friction: number, maxSpeed: number): void {
  const carried = Math.sqrt(body.vel.x * body.vel.x + body.vel.y * body.vel.y) * friction;
  let vx = (body.vel.x + dir.x * accel) * friction;
  let vy = (body.vel.y + dir.y * accel) * friction;
  const speed = Math.sqrt(vx * vx + vy * vy);
  const cap = Math.max(maxSpeed, carried);
  if (speed > cap) {
    vx = (vx / speed) * cap;
    vy = (vy / speed) * cap;
  }
  body.vel.x = vx;
  body.vel.y = vy;
}

/** Velocity decay per tick; a crawling body comes to rest so positions settle to clean values. */
export function applyFriction(body: Body, friction: number): void {
  body.vel.x *= friction;
  body.vel.y *= friction;
  if (body.vel.x * body.vel.x + body.vel.y * body.vel.y < REST_SPEED * REST_SPEED) {
    body.vel.x = 0;
    body.vel.y = 0;
  }
}

/** An impulse is a momentum change: the velocity change scales with 1 / mass. */
export function applyImpulse(body: Body, impulse: Vec2): void {
  body.vel.x += impulse.x / body.mass;
  body.vel.y += impulse.y / body.mass;
}

/**
 * Moves a body by one tick of its velocity through the tile grid. A blocked axis zeroes that
 * velocity component, so bodies rest against walls instead of storing speed in them.
 */
export function moveBody(map: TileMap, body: Body): void {
  const dx = body.vel.x / TICK_HZ;
  const dy = body.vel.y / TICK_HZ;
  const next = moveCircle(map, body.pos.x, body.pos.y, body.radius, dx, dy);
  if (Math.abs(next.x - body.pos.x - dx) > 1e-3) body.vel.x = 0;
  if (Math.abs(next.y - body.pos.y - dy) > 1e-3) body.vel.y = 0;
  body.pos.x = next.x;
  body.pos.y = next.y;
}

interface Contact {
  nx: number;
  ny: number;
  overlap: number;
}

function contact(a: Circle, b: Circle): Contact | null {
  const dx = b.pos.x - a.pos.x;
  const dy = b.pos.y - a.pos.y;
  const rsum = a.radius + b.radius;
  const d2 = dx * dx + dy * dy;
  if (d2 >= rsum * rsum) return null;
  const d = Math.sqrt(d2);
  // coincident centres: pick a fixed direction so the result stays deterministic
  if (d < 1e-9) return { nx: 1, ny: 0, overlap: rsum };
  return { nx: dx / d, ny: dy / d, overlap: rsum - d };
}

/** Exchanges momentum between two touching bodies that approach each other (inelastic bump). */
export function bounce(a: Body, b: Body): void {
  const c = contact(a, b);
  if (!c) return;
  const vn = (b.vel.x - a.vel.x) * c.nx + (b.vel.y - a.vel.y) * c.ny;
  if (vn >= 0) return;
  const j = (-(1 + RESTITUTION) * vn) / (1 / a.mass + 1 / b.mass);
  a.vel.x -= (j * c.nx) / a.mass;
  a.vel.y -= (j * c.ny) / a.mass;
  b.vel.x += (j * c.nx) / b.mass;
  b.vel.y += (j * c.ny) / b.mass;
}

/**
 * Pushes two overlapping bodies apart along their normal, the lighter one further. Displacements go
 * through the tile grid, and whatever one body cannot yield (wall behind it) the other takes.
 */
export function separate(map: TileMap, a: Body, b: Body): void {
  const c = contact(a, b);
  if (!c) return;
  const invA = 1 / a.mass;
  const invB = 1 / b.mass;
  const shareA = (c.overlap * invA) / (invA + invB);
  const beforeA = a.pos.x * c.nx + a.pos.y * c.ny;
  const movedA = moveCircle(map, a.pos.x, a.pos.y, a.radius, -c.nx * shareA, -c.ny * shareA);
  a.pos.x = movedA.x;
  a.pos.y = movedA.y;
  const yieldedA = beforeA - (a.pos.x * c.nx + a.pos.y * c.ny);
  const remaining = Math.max(0, c.overlap - yieldedA);
  const movedB = moveCircle(map, b.pos.x, b.pos.y, b.radius, c.nx * remaining, c.ny * remaining);
  b.pos.x = movedB.x;
  b.pos.y = movedB.y;
}

/** A body pushed out of a pillar lands this far outside it, so rounding never leaves it overlapping. */
const PILLAR_SKIN = 0.01;

/**
 * Pillars never move: a body overlapping one is pushed straight out (through the grid, so it cannot
 * land in a wall) and loses the part of its velocity that points into the pillar, so it slides
 * along the pillar the way it slides along a wall.
 */
export function pushOutOfPillars(map: TileMap, pillars: readonly Pillar[], body: Body): void {
  for (const pillar of pillars) {
    const c = contact(pillar, body);
    if (!c) continue;
    const push = c.overlap + PILLAR_SKIN;
    const moved = moveCircle(map, body.pos.x, body.pos.y, body.radius, c.nx * push, c.ny * push);
    body.pos.x = moved.x;
    body.pos.y = moved.y;
    const into = body.vel.x * c.nx + body.vel.y * c.ny;
    if (into < 0) {
      body.vel.x -= into * c.nx;
      body.vel.y -= into * c.ny;
    }
  }
}

/**
 * Bump and separate every pair, a few passes so chains (player → crate → crate) settle. The pillars
 * have the last word in each pass: nothing ends a pass inside one.
 */
export function resolveBodies(map: TileMap, pillars: readonly Pillar[], bodies: Body[]): void {
  for (let i = 0; i < bodies.length; i++) {
    for (let j = i + 1; j < bodies.length; j++) bounce(bodies[i], bodies[j]);
  }
  for (let pass = 0; pass < SEPARATION_ITERATIONS; pass++) {
    for (let i = 0; i < bodies.length; i++) {
      for (let j = i + 1; j < bodies.length; j++) separate(map, bodies[i], bodies[j]);
    }
    for (const body of bodies) pushOutOfPillars(map, pillars, body);
  }
}
